'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2DiffStats } from '@/lib/pr-v2/test-file-filter';
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { Eye, EyeOff } from 'lucide-react';
import type { PrV2MetadataSummary } from '../shared/pr-v2-metadata';
import { usePrV2FileFilterSnapshot } from '../pr-v2-file-filter-context';
import { DiffStatsBreakdown } from '@/components/shared/diff/diff-stats-breakdown';
import { DiffLineStats } from '@/components/ui/diff-line-stats';
import { RollingNumber } from '@/components/ui/rolling-number';
import { Skeleton } from '@/components/ui/skeleton';
import { BetterTooltip } from '@/components/ui/tooltip';
import { usePrV2CodeChanges } from '@/data/v2/pr/use-pr-v2-code-changes';
import { isTestFile } from '@/lib/file-paths/is-test-file';
import { createPrV2FileFilter, getPrV2DiffStats } from '@/lib/pr-v2/test-file-filter';
import { usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';
type PrV2HeaderDiffStatsProps = {
  owner: string;
  repo: string;
  number: string;
  summary: PrV2SummaryResponse['pullRequest'];
  metadata: PrV2MetadataSummary;
};
function HeaderLineStats(t0) {
  const $ = _c(10);
  const {
    additions,
    deletions,
    breakdown
  } = t0;
  let t1;
  if ($[0] !== additions || $[1] !== deletions) {
    t1 = <DiffLineStats animated={true} additions={additions} deletions={deletions} />;
    $[0] = additions;
    $[1] = deletions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const lineStats = t1;
  if (!breakdown) {
    return lineStats;
  }
  let t2;
  if ($[3] !== breakdown) {
    t2 = <DiffStatsBreakdown stats={breakdown} />;
    $[3] = breakdown;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== lineStats) {
    t3 = <span className="inline-flex cursor-default" data-testid="pr-v2-diff-line-stats-trigger">{lineStats}</span>;
    $[5] = lineStats;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <BetterTooltip content={t2}>{t3}</BetterTooltip>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * The header's file and line counts, which follow the test-file filter, plus the toggle for it.
 * The toggle's and the line counts' tooltips break the diff down into code, tests, and total.
 */
export function PrV2HeaderDiffStats(t0) {
  const $ = _c(28);
  const {
    owner,
    repo,
    number,
    summary,
    metadata
  } = t0;
  const hideTestFiles = usePrV2ViewPreferencesStore(_temp);
  const applyPreferences = usePrV2ViewPreferencesStore(_temp2);
  const snapshot = usePrV2FileFilterSnapshot();
  let t1;
  if ($[0] !== summary.baseSha || $[1] !== summary.headSha) {
    t1 = {
      headSha: summary.headSha,
      baseSha: summary.baseSha
    };
    $[0] = summary.baseSha;
    $[1] = summary.headSha;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const canonicalQuery = usePrV2CodeChanges(owner, repo, number, t1);
  const files = snapshot ? snapshot.status === "ready" ? snapshot.files : null : canonicalQuery.data?.files;
  let t2;
  if ($[3] !== files || $[4] !== hideTestFiles || $[5] !== snapshot?.revealedFilenames) {
    t2 = files ? getPrV2DiffStats(files, createPrV2FileFilter(hideTestFiles, snapshot?.revealedFilenames ?? [])) : null;
    $[3] = files;
    $[4] = hideTestFiles;
    $[5] = snapshot?.revealedFilenames;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const diffStats = t2;
  const stats = diffStats?.visible;
  let t3;
  if ($[7] !== canonicalQuery.data) {
    t3 = canonicalQuery.data ? canonicalQuery.data.files.filter(_temp3).length : null;
    $[7] = canonicalQuery.data;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const testFileCount = t3;
  const countsLoading = snapshot ? snapshot.status === "loading" : hideTestFiles && !stats && !canonicalQuery.error;
  const countsError = snapshot ? snapshot.status === "error" : hideTestFiles && !stats && Boolean(canonicalQuery.error);
  const fileCount = stats?.fileCount ?? metadata.changedFiles;
  const toggleLabel = hideTestFiles ? "Show test files" : "Hide test files";
  const hasTestFiles = testFileCount !== null && testFileCount > 0;
  let t4;
  if ($[9] !== countsError || $[10] !== countsLoading || $[11] !== fileCount) {
    t4 = countsLoading ? <Skeleton className="h-5 w-12" aria-label="Loading file counts" /> : countsError ? <span>File count unavailable</span> : fileCount !== null ? <span className="inline-flex items-center gap-1"><RollingNumber value={String(fileCount)} /><span>{fileCount === 1 ? "file" : "files"}</span></span> : null;
    $[9] = countsError;
    $[10] = countsLoading;
    $[11] = fileCount;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = stats?.additions ?? metadata.additions ?? 0;
  const t6 = stats?.deletions ?? metadata.deletions ?? 0;
  const t7 = hasTestFiles ? diffStats : null;
  let t8;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <HeaderLineStats additions={t5} deletions={t6} breakdown={t7} />;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== applyPreferences || $[18] !== diffStats || $[19] !== hasTestFiles || $[20] !== hideTestFiles || $[21] !== testFileCount || $[22] !== toggleLabel) {
    t9 = hasTestFiles ? <BetterTooltip content={diffStats ? <DiffStatsBreakdown stats={diffStats} /> : toggleLabel}><button type="button" aria-pressed={hideTestFiles} className="inline-flex items-center gap-1 text-sm underline-offset-2 hover:text-zinc-200 hover:underline focus-visible:underline" onClick={() => applyPreferences({
        hideTestFiles: !hideTestFiles
      })}>{hideTestFiles ? <EyeOff className="size-3" aria-hidden="true" /> : <Eye className="size-3" aria-hidden="true" />}{`${testFileCount} ${testFileCount === 1 ? "test" : "tests"}`}</button></BetterTooltip> : null;
    $[17] = applyPreferences;
    $[18] = diffStats;
    $[19] = hasTestFiles;
    $[20] = hideTestFiles;
    $[21] = testFileCount;
    $[22] = toggleLabel;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t4 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400" data-testid="pr-v2-visible-file-stats">{t4}{t8}{t9}</div>;
    $[24] = t4;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function _temp3(file) {
  return isTestFile(file.filename);
}
function _temp2(state_0) {
  return state_0.applyPreferences;
}
function _temp(state) {
  return state.hideTestFiles;
}